import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    InputAdornment,
    InputBase,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Menu,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import SearchIcon from '@mui/icons-material/Search';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import type { ConversacionIaResumen } from '../../types/consultasIa';

interface HistorialConversacionesIaProps {
    conversaciones: ConversacionIaResumen[];
    seleccionadaId: string | null;
    cargando?: boolean;
    deshabilitado?: boolean;
    onSeleccionar: (id: string) => void;
    onNueva: () => void;
    onRenombrar: (id: string, titulo: string) => void;
    onEliminar: (id: string) => void;
}

const inicioDelDia = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

const grupoDeFecha = (iso: string): string => {
    const hoy = inicioDelDia(new Date());
    const dia = inicioDelDia(new Date(iso));
    const diffDias = Math.round((hoy - dia) / 86400000);
    if (diffDias <= 0) return 'Hoy';
    if (diffDias === 1) return 'Ayer';
    if (diffDias <= 7) return 'Últimos 7 días';
    if (diffDias <= 30) return 'Últimos 30 días';
    return 'Anteriores';
};

const HistorialConversacionesIa: React.FC<HistorialConversacionesIaProps> = ({
    conversaciones,
    seleccionadaId,
    cargando = false,
    deshabilitado = false,
    onSeleccionar,
    onNueva,
    onRenombrar,
    onEliminar,
}) => {
    const [busqueda, setBusqueda] = useState('');
    const [menu, setMenu] = useState<{ anchor: HTMLElement; id: string } | null>(null);
    const [editandoId, setEditandoId] = useState<string | null>(null);
    const [tituloEdicion, setTituloEdicion] = useState('');
    const [eliminarId, setEliminarId] = useState<string | null>(null);

    const grupos = useMemo(() => {
        const q = busqueda.trim().toLowerCase();
        const filtradas = q ? conversaciones.filter((c) => c.titulo.toLowerCase().includes(q)) : conversaciones;
        const mapa = new Map<string, ConversacionIaResumen[]>();
        filtradas.forEach((c) => {
            const g = grupoDeFecha(c.actualizadaEn);
            mapa.set(g, [...(mapa.get(g) ?? []), c]);
        });
        return Array.from(mapa.entries());
    }, [conversaciones, busqueda]);

    const iniciarEdicion = (id: string) => {
        const c = conversaciones.find((x) => x.id === id);
        setTituloEdicion(c?.titulo ?? '');
        setEditandoId(id);
        setMenu(null);
    };

    const confirmarEdicion = () => {
        const titulo = tituloEdicion.trim();
        if (editandoId && titulo) onRenombrar(editandoId, titulo);
        setEditandoId(null);
    };

    const tituloAEliminar = conversaciones.find((c) => c.id === eliminarId)?.titulo;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#fafafa' }}>
            <Box sx={{ p: 1.5, pb: 1 }}>
                <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    startIcon={<AddCommentOutlinedIcon />}
                    onClick={onNueva}
                    disabled={deshabilitado}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500, boxShadow: 'none', py: 1 }}
                >
                    Nueva conversación
                </Button>
                <InputBase
                    fullWidth
                    placeholder="Buscar conversaciones"
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    startAdornment={
                        <InputAdornment position="start">
                            <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                    }
                    sx={{
                        mt: 1.5,
                        px: 1.25,
                        py: 0.5,
                        fontSize: '0.875rem',
                        bgcolor: '#ffffff',
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                    }}
                />
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', px: 1 }}>
                {cargando ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : grupos.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                        {busqueda.trim()
                            ? 'No hay conversaciones que coincidan.'
                            : 'Todavía no tenés conversaciones guardadas.'}
                    </Typography>
                ) : (
                    grupos.map(([grupo, items]) => (
                        <List
                            key={grupo}
                            dense
                            disablePadding
                            subheader={
                                <ListSubheader
                                    disableSticky
                                    sx={{
                                        bgcolor: 'transparent',
                                        lineHeight: '32px',
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                    }}
                                >
                                    {grupo}
                                </ListSubheader>
                            }
                        >
                            {items.map((c) =>
                                editandoId === c.id ? (
                                    <Box key={c.id} sx={{ px: 1, py: 0.5 }}>
                                        <TextField
                                            autoFocus
                                            fullWidth
                                            size="small"
                                            value={tituloEdicion}
                                            onChange={(e) => setTituloEdicion(e.target.value)}
                                            onBlur={confirmarEdicion}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') confirmarEdicion();
                                                if (e.key === 'Escape') setEditandoId(null);
                                            }}
                                            inputProps={{ maxLength: 80 }}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#ffffff' } }}
                                        />
                                    </Box>
                                ) : (
                                    <ListItemButton
                                        key={c.id}
                                        selected={c.id === seleccionadaId}
                                        disabled={deshabilitado}
                                        onClick={() => onSeleccionar(c.id)}
                                        sx={{
                                            borderRadius: 2,
                                            mb: 0.25,
                                            pr: 0.5,
                                            '& .acciones': { opacity: 0 },
                                            '&:hover .acciones, &.Mui-selected .acciones': { opacity: 1 },
                                            '&.Mui-selected': { bgcolor: '#e3f2fd' },
                                            '&.Mui-selected:hover': { bgcolor: '#d6eafc' },
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 30 }}>
                                            <ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={c.titulo}
                                            primaryTypographyProps={{ noWrap: true, fontSize: '0.875rem' }}
                                        />
                                        <IconButton
                                            className="acciones"
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setMenu({ anchor: e.currentTarget, id: c.id });
                                            }}
                                        >
                                            <MoreHorizIcon fontSize="small" />
                                        </IconButton>
                                    </ListItemButton>
                                ),
                            )}
                        </List>
                    ))
                )}
            </Box>
            <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)}>
                <MenuItem onClick={() => menu && iniciarEdicion(menu.id)}>
                    <ListItemIcon>
                        <EditOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    Renombrar
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        if (menu) setEliminarId(menu.id);
                        setMenu(null);
                    }}
                    sx={{ color: 'error.main' }}
                >
                    <ListItemIcon>
                        <DeleteOutlineIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    Eliminar
                </MenuItem>
            </Menu>

            <Dialog open={!!eliminarId} onClose={() => setEliminarId(null)} maxWidth="xs" fullWidth>
                <DialogTitle>¿Eliminar conversación?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Se eliminará <strong>{tituloAEliminar}</strong>. Esta acción no se puede deshacer.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEliminarId(null)} sx={{ textTransform: 'none' }}>
                        Cancelar
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={() => {
                            if (eliminarId) onEliminar(eliminarId);
                            setEliminarId(null);
                        }}
                        sx={{ textTransform: 'none' }}
                    >
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default HistorialConversacionesIa;
